// src/business-types/car-wash/modules/carwash/components/OrderSummary.tsx
import React, { useState, useEffect } from "react";
import CarItem from "./CarItem";
import { MultiCarItem } from "../../home/components/types";
import { toast } from "react-toastify";
import { Product } from "src/shared/services/interfacev2";
import { useUserTheme } from "src/shared/hooks/useUserTheme";

interface OrderSummaryProps {
  Name?: string;
  totalPrice: number | string;
  multiCar: MultiCarItem[];
  isEditing: boolean;
  editCarIndex: number | null;
  onEdit: (index: number) => void;
  onDeleteCar: (index: number) => void;
onDeleteProduct: (product: Product, carIndex: number) => void;  onAddCar: () => void;
  onNext: () => void;
  isNextDisabled: boolean;
  onPaymentMethodChange?: (method: "cash" | "Buy on credit") => void;
  selectedPaymentMethod?: "cash" | "Buy on credit" | null;
}

const OrderSummary: React.FC<OrderSummaryProps> = ({
  Name,
  totalPrice,
  multiCar,
  isEditing,
  editCarIndex,
  onEdit,
  onDeleteCar,
  onDeleteProduct,
  onAddCar,
  onNext,
  isNextDisabled,
  onPaymentMethodChange,
}) => {
  const [paymentMethod, setPaymentMethod] = useState<"cash" | "Buy on credit" | null>(null);
  const { bgClass, fontClass} = useUserTheme();

  // ✅ handle payment selection
  const handlePaymentMethodChange = (method: "cash" | "Buy on credit") => {
    setPaymentMethod(method);
    onPaymentMethodChange?.(method);
  };

  // ✅ reset payment method when all cars are deleted
  useEffect(() => {
    if (multiCar.length === 0) {
      setPaymentMethod(null);
    }
  }, [multiCar.length]);

  // ✅ check that all cars have at least one selected service
  const validateCarsBeforeNext = (): boolean => {
    const carsWithoutServices = multiCar.filter((car) => !car.productDetails || car.productDetails.length === 0).map((car) => car.carNumber || "Unnamed Car");

    if (carsWithoutServices.length > 0) {
      const list = carsWithoutServices.join(", ");
      toast.warning(`Please select at least one service for: ${list}`);
      return false;
    }

    return true;
  };

  // ✅ Handle Next button click
  const handleNextClick = () => {
    if (multiCar.length === 0) {
      toast.warning("Please add at least one car before proceeding.");
      return;
    }

    const valid = validateCarsBeforeNext();
    if (!valid) return;

    if (!paymentMethod) {
      toast.warning("Please select a payment method before proceeding.");
      return;
    }

    onNext();
  };

  const isNextButtonEnabled = !isNextDisabled && multiCar.length > 0;

  return (
    <div className="right-panel flex flex-col h-full w-full bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden" style={{ height: "650px" }}>
      {/* Header */}
      <div className={`${bgClass} ${fontClass} p-4 text-white rounded-t-xl`}>
        <div className="flex justify-between items-start mb-3">
          <h2 className="text-xl font-bold">Order Summary</h2>
          <div className="text-right">
            <p className="text-blue-100 text-xs mb-1">Total Items: {multiCar.length} • Customer</p>
            <p className="text-2xl font-extrabold">{typeof totalPrice === "number" ? totalPrice.toFixed(2) : totalPrice}</p>
          </div>
        </div>

        <div className="flex justify-between items-center">
          <div>
            <p className="text-blue-100 text-sm mb-1">Customer</p>
            <p className="font-semibold text-base truncate max-w-[180px]">{Name || "Guest"}</p>
          </div>

          <div className="bg-white/20 px-3 py-1 rounded-full">
            <span className="text-sm font-medium">
              {multiCar.length} car{multiCar.length !== 1 ? "s" : ""}
            </span>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 flex flex-col p-4 gap-4 overflow-y-auto">
        {/* Cars List */}
        <div className="space-y-4">
          {multiCar.length === 0 ? (
            <div className="h-40 flex flex-col justify-center items-center p-6 text-center bg-gray-50 border border-gray-200 rounded-xl">
              <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center mb-3">
                <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                </svg>
              </div>
              <p className="text-gray-600 font-medium text-sm mb-1">No cars added</p>
              <p className="text-gray-400 text-xs">Click "ADD CAR" to get started</p>
            </div>
          ) : (
            <div className="space-y-3">
              {multiCar.map((car, index) => (
                <CarItem key={index} car={car} index={index} isEditing={isEditing} isActiveEdit={editCarIndex === index} onEdit={onEdit} onDelete={onDeleteCar} onDeleteProduct={onDeleteProduct} />
              ))}
            </div>
          )}
        </div>

        {/* Grand Total */}
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
          <div className="flex justify-between items-center">
            <span className="text-lg font-semibold text-gray-800">Grand Total</span>
            <span className="text-2xl font-bold text-gray-900">{typeof totalPrice === "number" ? totalPrice.toFixed(2) : totalPrice}</span>
          </div>
        </div>

        {/* Payment Method Section */}
        <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
          <h4 className="text-lg font-semibold text-gray-800 mb-3">Select Payment Method</h4>
          <div className="flex gap-4">
            {["cash", "Buy on credit"].map((method) => (
              <label key={method} className="flex items-center cursor-pointer group">
                <div className="relative">
                  <input
                    type="radio"
                    name="paymentMethod"
                    value={method}
                    checked={paymentMethod === method}
                    onChange={() => handlePaymentMethodChange(method as "cash" | "Buy on credit")}
                    className="sr-only"
                  />
                  <div
                    className={`w-5 h-5 border-2 rounded-full flex items-center justify-center transition-all duration-200 ${
                      paymentMethod === method ? "border-blue-500 bg-blue-500" : "border-gray-300 bg-white group-hover:border-gray-400"
                    }`}
                  >
                    {paymentMethod === method && <div className="w-2 h-2 bg-white rounded-full"></div>}
                  </div>
                </div>
                <span className="ml-2 text-sm font-medium text-gray-700 capitalize group-hover:text-gray-900 transition-colors duration-200">{method}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="grid grid-cols-2 gap-3 mt-auto">
          <button
            onClick={onAddCar}
            className="flex items-center justify-center gap-2 py-3.5 rounded-xl bg-gradient-to-r from-green-500 to-green-600 text-white font-semibold text-base shadow-sm hover:shadow-md hover:from-green-600 hover:to-green-700 transition-all duration-200 active:scale-95"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            ADD CAR
          </button>

          <button
            onClick={handleNextClick}
            disabled={!isNextButtonEnabled}
            className={`flex items-center justify-center gap-2 py-3.5 rounded-xl font-semibold text-base transition-all duration-200 ${
              !isNextButtonEnabled
                ? "bg-gray-200 text-gray-400 cursor-not-allowed shadow-sm"
                : "bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-sm hover:shadow-md hover:from-blue-600 hover:to-blue-700 active:scale-95"
            }`}
          >
            NEXT
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrderSummary;
